import React, { useEffect, useMemo, useState } from 'react';

const TOKEN_KEY = 'meo_admin_publish_token';

interface AuditRow {
  token: string;
  station: string;
  keyword: string;
  store_title: string;
  place_id: string;
  health_score: number;
  health_segment: 'unmanaged' | 'amateur' | 'agency' | 'optimized';
  view_count: number;
  unique_visitors: number;
  published_at: string;
  last_viewed_at: string | null;
  expires_at: string | null;
}

const SEGMENT_LABEL: Record<AuditRow['health_segment'], { label: string; color: string }> = {
  unmanaged: { label: '完全未対応',   color: 'text-rose-700' },
  amateur:   { label: '素人運用',     color: 'text-amber-700' },
  agency:    { label: '代理店運用中', color: 'text-brand-700' },
  optimized: { label: '強力対策済',   color: 'text-leaf-700' },
};

const TokenLogin: React.FC<{ onSubmit: (t: string) => void; error?: string }> = ({ onSubmit, error }) => {
  const [val, setVal] = useState('');
  return (
    <div className="min-h-screen bg-white">
      <AdminTopBar showLogout={false} />
      <div className="max-w-md mx-auto px-6 py-16">
        <h1 className="text-xl font-bold text-slate-900 mb-2">管理画面ログイン</h1>
        <p className="text-[13px] text-slate-500 mb-6 leading-relaxed">
          ローカルの <code className="bg-slate-100 px-1.5 py-0.5 text-xs">.env</code> に書いた
          <code className="bg-slate-100 px-1.5 py-0.5 text-xs ml-1">PUBLISH_TOKEN</code> を入力してください。
          一度入力するとブラウザに保存され、以降はこのページに直接アクセスできます。
        </p>
        <input
          type="password"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && val.trim() && onSubmit(val.trim())}
          placeholder="PUBLISH_TOKEN"
          className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:border-brand-500"
        />
        {error && (
          <p className="text-xs text-rose-700 mt-2">{error}</p>
        )}
        <button
          type="button"
          disabled={!val.trim()}
          onClick={() => onSubmit(val.trim())}
          className="w-full mt-4 px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded hover:bg-brand-600 disabled:opacity-40"
        >
          ログイン
        </button>
      </div>
    </div>
  );
};

const AdminTopBar: React.FC<{ showLogout: boolean; onLogout?: () => void }> = ({ showLogout, onLogout }) => (
  <div
    className="px-3 py-2 flex items-center justify-between sticky top-0 z-50"
    style={{ background: '#0B1F3B' }}
  >
    <div className="flex items-center gap-3">
      <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-white/10 text-white border border-white/30">
        Admin
      </span>
      <span className="text-[11px] text-white/80">MEO Audit</span>
    </div>
    <div className="flex items-center gap-3">
      {showLogout && (
        <button
          type="button"
          onClick={onLogout}
          className="text-[11px] text-white/70 hover:text-white px-2 py-0.5 rounded border border-white/20 hover:border-white/50"
        >
          ログアウト
        </button>
      )}
      <img
        src="/Clariveil.png"
        alt="Clariveil"
        style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
      />
    </div>
  </div>
);

const AdminAuditsPage: React.FC = () => {
  const [token, setToken] = useState<string | null>(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
    if (stored) return stored;
    const fromUrl = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('key')
      : null;
    return fromUrl;
  });
  const [audits, setAudits] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterSegment, setFilterSegment] = useState<string>('all');
  const [filterStation, setFilterStation] = useState<string>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!token) return;
    localStorage.setItem(TOKEN_KEY, token);
    setLoading(true);
    setError('');
    fetch('/api/audits?limit=200', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (r.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          throw new Error('PUBLISH_TOKEN が無効です。再度入力してください。');
        }
        if (!r.ok) throw new Error('読み込み失敗');
        return (await r.json()) as { audits: AuditRow[] };
      })
      .then((d) => setAudits(d.audits))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const stations = useMemo(() => {
    const set = new Set(audits.map((a) => a.station));
    return Array.from(set).sort();
  }, [audits]);

  const filtered = useMemo(() => {
    return audits.filter((a) => {
      if (filterSegment !== 'all' && a.health_segment !== filterSegment) return false;
      if (filterStation !== 'all' && a.station !== filterStation) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!a.store_title.toLowerCase().includes(q) && !a.keyword.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [audits, filterSegment, filterStation, search]);

  if (!token) {
    return <TokenLogin onSubmit={setToken} error={error} />;
  }

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <div className="min-h-screen bg-white">
      <AdminTopBar showLogout onLogout={handleLogout} />

      {/* Title */}
      <div className="bg-white border-b border-slate-200 px-6 py-5">
        <div className="text-[11px] text-slate-500 mb-1 tracking-[0.08em] uppercase">
          管理画面 / 発行済みレポート
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-1">発行済みオーディット一覧</h1>
        <p className="text-[13px] text-slate-500 leading-relaxed">
          公開されているMEO診断レポートの一覧と閲覧状況。
          行をクリックすると別タブで共有URLが開きます。
        </p>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-slate-200">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="店舗名・キーワード検索…"
            className="flex-1 min-w-[200px] px-3 py-1.5 border border-slate-300 rounded text-[13px] focus:outline-none focus:border-brand-500"
          />
          <select
            value={filterStation}
            onChange={(e) => setFilterStation(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded text-[13px] bg-white"
          >
            <option value="all">全駅</option>
            {stations.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={filterSegment}
            onChange={(e) => setFilterSegment(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded text-[13px] bg-white"
          >
            <option value="all">全セグメント</option>
            <option value="unmanaged">完全未対応</option>
            <option value="amateur">素人運用</option>
            <option value="agency">代理店運用中</option>
            <option value="optimized">強力対策済</option>
          </select>
          <span className="text-[11px] text-slate-500">
            表示中 {filtered.length} / 全 {audits.length} 件
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-sm text-slate-500">読み込み中…</div>
        ) : error ? (
          <div className="text-center py-12 text-sm text-rose-700">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500">
            {audits.length === 0 ? 'まだ発行されたレポートはありません。' : '該当するレポートがありません。'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600">
                  <th className="text-left py-2 px-2 font-semibold">店舗</th>
                  <th className="text-left py-2 px-2 font-semibold">駅</th>
                  <th className="text-left py-2 px-2 font-semibold">キーワード</th>
                  <th className="text-right py-2 px-2 font-semibold">スコア</th>
                  <th className="text-left py-2 px-2 font-semibold">セグメント</th>
                  <th className="text-right py-2 px-2 font-semibold">PV</th>
                  <th className="text-right py-2 px-2 font-semibold">UU</th>
                  <th className="text-left py-2 px-2 font-semibold">最終閲覧</th>
                  <th className="text-left py-2 px-2 font-semibold">公開日</th>
                  <th className="text-left py-2 px-2 font-semibold">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => {
                  const seg = SEGMENT_LABEL[a.health_segment];
                  const shareUrl = `${window.location.origin}/share/audit/${a.token}`;
                  return (
                    <tr
                      key={a.token}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => window.open(shareUrl, '_blank', 'noopener')}
                    >
                      <td className="py-2 px-2 text-slate-900 font-medium">{a.store_title}</td>
                      <td className="py-2 px-2 text-slate-700">{a.station}</td>
                      <td className="py-2 px-2 text-slate-700">{a.keyword}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{a.health_score}</td>
                      <td className={`py-2 px-2 text-[11px] ${seg.color}`}>{seg.label}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{a.view_count}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{a.unique_visitors}</td>
                      <td className="py-2 px-2 text-slate-500 text-[11px]">
                        {a.last_viewed_at ? a.last_viewed_at.replace('T', ' ').slice(0, 16) : '-'}
                      </td>
                      <td className="py-2 px-2 text-slate-500 text-[11px]">
                        {a.published_at.replace('T', ' ').slice(0, 16)}
                      </td>
                      <td className="py-2 px-2 text-[11px]">
                        <a
                          href={shareUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-brand-600 hover:underline mr-3"
                        >
                          開く
                        </a>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(shareUrl);
                          }}
                          className="text-slate-500 hover:text-slate-900"
                        >
                          URLコピー
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <footer className="py-6 mt-8 text-center text-xs text-slate-400 border-t border-slate-200">
        Powered by Clariveil
      </footer>
    </div>
  );
};

export default AdminAuditsPage;
